import React, { useState } from 'react';
import { Database, RefreshCw, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { adminAPI, accountsAPI } from '../../lib/api';
import HistoricalImportWizard from '../HistoricalImportWizard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

export default function DataTab({ stats, loadData }) {
  const [processing, setProcessing] = useState(false);
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [showClearGLConfirm, setShowClearGLConfirm] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);

  const handleSeedAccounts = async () => {
    setProcessing(true);
    try {
      const res = await accountsAPI.seed();
      toast.success(res.data.message);
      setShowSeedConfirm(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed accounts');
    } finally {
      setProcessing(false);
    }
  };

  const handleClearGL = async () => {
    setProcessing(true);
    try {
      const res = await adminAPI.clearGL();
      toast.success(res.data.message);
      setShowClearGLConfirm(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear GL');
    } finally {
      setProcessing(false);
    }
  };

  const handleClearAll = async () => {
    setProcessing(true);
    try {
      const res = await adminAPI.clearAllTransactions();
      toast.success(res.data.message);
      setShowClearAllConfirm(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <HistoricalImportWizard onComplete={loadData} />

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Database className="w-5 h-5 text-[#D94E18]" />Data Management</CardTitle>
            <CardDescription>Manage system data. These operations affect all users.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats && (
              <div className="p-4 bg-zinc-50 rounded-lg">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">Current Data</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-zinc-500">Transactions</p><p className="font-mono font-semibold">{stats.transactions?.total || 0}</p></div>
                  <div><p className="text-zinc-500">Journal Entries</p><p className="font-mono font-semibold">{stats.journal_entries || 0}</p></div>
                  <div><p className="text-zinc-500">Accounts</p><p className="font-mono font-semibold">{stats.accounts || 0}</p></div>
                  <div><p className="text-zinc-500">Documents</p><p className="font-mono font-semibold">{stats.source_documents || 0}</p></div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between p-4 border border-zinc-200 rounded-lg">
              <div><p className="font-medium text-sm">Re-seed Default Accounts</p><p className="text-xs text-zinc-500 mt-0.5">Create missing default chart of accounts.</p></div>
              <Button variant="outline" size="sm" onClick={() => setShowSeedConfirm(true)} data-testid="seed-accounts-action-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Seed</Button>
            </div>
            <div className="flex items-center justify-between p-4 border border-amber-200 rounded-lg bg-amber-50/50">
              <div><div className="flex items-center gap-2"><p className="font-medium text-sm">Clear General Ledger</p><Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Caution</Badge></div><p className="text-xs text-zinc-500 mt-0.5">Delete journal entries, reset balances.</p></div>
              <Button variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-100" onClick={() => setShowClearGLConfirm(true)} data-testid="clear-gl-btn"><RefreshCw className="w-4 h-4 mr-1.5" />Clear GL</Button>
            </div>
            <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50/50">
              <div><div className="flex items-center gap-2"><p className="font-medium text-sm">Delete All Transaction Data</p><Badge variant="outline" className="text-red-600 border-red-300 text-xs">Destructive</Badge></div><p className="text-xs text-zinc-500 mt-0.5">Deletes transactions, journal entries, invoices, customers, and documents. Cannot be undone.</p></div>
              <Button variant="outline" size="sm" className="border-red-400 text-red-700 hover:bg-red-100" onClick={() => setShowClearAllConfirm(true)} data-testid="clear-all-btn"><Trash2 className="w-4 h-4 mr-1.5" />Delete All</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seed Accounts Dialog */}
      <AlertDialog open={showSeedConfirm} onOpenChange={setShowSeedConfirm}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Re-seed Default Accounts?</AlertDialogTitle><AlertDialogDescription>Create missing default accounts. Existing accounts won't be modified.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleSeedAccounts} disabled={processing} className="bg-[#D94E18] hover:bg-[#C2410C]" data-testid="confirm-seed-btn">{processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Seed Accounts</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Clear GL Dialog */}
      <AlertDialog open={showClearGLConfirm} onOpenChange={setShowClearGLConfirm}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" />Clear General Ledger?</AlertDialogTitle><AlertDialogDescription>Delete all journal entries and reset balances. Cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleClearGL} disabled={processing} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-clear-gl-btn">{processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Clear GL</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Clear All Dialog */}
      <AlertDialog open={showClearAllConfirm} onOpenChange={setShowClearAllConfirm}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" />Delete ALL Data?</AlertDialogTitle><AlertDialogDescription>Permanently delete all transactions, journal entries, invoices, customers, and documents. Cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleClearAll} disabled={processing} className="bg-red-600 hover:bg-red-700" data-testid="confirm-clear-all-btn">{processing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Delete Everything</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </>
  );
}
